// src/components/CredentialManagement/modals/AddCredentialModal.js
import React, { useState } from "react";
import config from "../../../config";
import ConfirmActionModal from "../../../ConfirmActionModal";
import "./AddCredentialModal.css";
import logger from "../../../utils/logger";

// Helper function to create composite key from deviceId and credentialId
const makePendingKey = (deviceId, credentialId) =>
  `${deviceId}:${credentialId}`;

const getCredentialLabel = (cred) => {
  if (cred.credentialType !== "PHYSICAL") {
    return cred.deviceType?.replace("_", " ") || "Unknown";
  }
  if (cred.bitFormat === "32X") {
    return `Physical Credential · 32X · ID: ${cred.badgeId}`;
  }
  if (cred.bitFormat === "48X") {
    return `Physical Credential · 48X · IC: ${cred.issueCode} · FC: ${cred.facilityCode} · ID: ${cred.badgeId}`;
  }
  return `Physical Credential · ${cred.bitFormat} · FC: ${cred.facilityCode} · ID: ${cred.badgeId}`;
};

const AddCredentialModal = ({
  patron,
  devices,
  credentials,
  onClose,
  onSuccess,
  pendingAdditionCredentials,
  setPendingAdditionCredentials,
}) => {
  const [selectedCredential, setSelectedCredential] = useState("");
  const [selectedDevices, setSelectedDevices] = useState(new Set());
  const [isAdding, setIsAdding] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const toggleDeviceSelection = (deviceId) => {
    setSelectedDevices((prev) => {
      const newSelected = new Set(prev);
      if (newSelected.has(deviceId)) {
        newSelected.delete(deviceId);
      } else {
        newSelected.add(deviceId);
      }
      return newSelected;
    });
  };

  const handleAddCredential = async () => {
    if (!selectedCredential) {
      alert("Please select a credential.");
      return;
    }

    if (selectedDevices.size === 0) {
      alert("Please select at least one device to add the credential to.");
      return;
    }

    // Check if any operations are already pending for these device-credential pairs
    if (pendingAdditionCredentials) {
      const alreadyPending = Array.from(selectedDevices).some((deviceId) => {
        const pendingKey = makePendingKey(deviceId, selectedCredential);
        return pendingAdditionCredentials.has(pendingKey);
      });

      if (alreadyPending) {
        alert(
          "One or more of the selected devices already have a pending addition operation for this credential. Please wait for the operation to complete."
        );
        return;
      }
    }

    // Show confirmation modal instead of window.confirm
    setShowConfirmModal(true);
  };

  const performAddCredential = async () => {
    setShowConfirmModal(false);
    setIsAdding(true);

    const deviceCount = selectedDevices.size;

    try {
      const addPromises = Array.from(selectedDevices).map(async (deviceId) => {
        const response = await fetch(
          `${config.apiEndpoint}/credentials/${selectedCredential}/devices/${deviceId}`,
          {
            method: "POST",
            headers: {
              "x-api-key": config.apiKey,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.message ||
              `Failed to add credential to device ${deviceId}`
          );
        }

        return response.json();
      });

      await Promise.all(addPromises);

      // Add device-credential pairs to pending addition set using composite keys
      if (setPendingAdditionCredentials) {
        setPendingAdditionCredentials((prev) => {
          const newPending = new Set(prev);
          selectedDevices.forEach((deviceId) => {
            const pendingKey = makePendingKey(deviceId, selectedCredential);
            newPending.add(pendingKey);
          });
          return newPending;
        });
      }

      // Show success modal instead of alert
      setSuccessMessage(
        `Credential successfully queued for addition to ${deviceCount} device${
          deviceCount > 1 ? "s" : ""
        }!`
      );
      setShowSuccessModal(true);

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      logger.error("Error adding credential to devices:", error);
      alert(`Failed to add credential: ${error.message}`);
    } finally {
      setIsAdding(false);
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    onClose();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const getCredentialDevices = (credId) => {
    const cred = credentials.find((c) => c.credentialId === credId);
    return cred?.deviceIds || [];
  };

  const isDeviceAlreadyHasCredential = (deviceId) => {
    if (!selectedCredential) return false;
    const credDevices = getCredentialDevices(selectedCredential);
    return credDevices.includes(deviceId);
  };

  return (
    <div
      className="add-credential-modal-backdrop"
      onClick={handleBackdropClick}
    >
      <div className="add-credential-modal">
        <div className="add-credential-modal-header">
          <div className="add-credential-modal-title-container">
            <h2>
              Add Credential to Devices -{" "}
              {patron?.badgePhotoUrl && (
                <img
                  src={patron.badgePhotoUrl}
                  alt={`${patron.firstName} ${patron.lastName}`}
                  className="add-credential-modal-photo"
                />
              )}
              {patron && `${patron.firstName} ${patron.lastName}`}
            </h2>
          </div>
          <button
            className="add-credential-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="add-credential-modal-body">
          <div className="credential-select-section">
            <label htmlFor="credential-select">
              <strong>Select Credential:</strong>
            </label>
            <select
              id="credential-select"
              value={selectedCredential}
              onChange={(e) => setSelectedCredential(e.target.value)}
              className="credential-select"
              disabled={isAdding}
            >
              <option value="">-- Choose a credential --</option>
              {credentials.map((cred) => (
                <option key={cred.credentialId} value={cred.credentialId}>
                  {getCredentialLabel(cred)} ({cred.credentialId})
                </option>
              ))}
            </select>
          </div>

          {selectedCredential && (
            <div className="device-select-section">
              <label>
                <strong>Select Devices:</strong>
              </label>
              <div className="device-selection-vertical">
                {devices && devices.length > 0 ? (
                  devices.map((device) => {
                    const alreadyHasCredential = isDeviceAlreadyHasCredential(
                      device.deviceId
                    );
                    const isSelected = selectedDevices.has(device.deviceId);

                    return (
                      <div
                        key={device.deviceId}
                        className={`add-device-card ${
                          alreadyHasCredential ? "device-already-has" : ""
                        } ${isSelected ? "device-selected" : ""}`}
                      >
                        <label className="add-device-card-label">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() =>
                              toggleDeviceSelection(device.deviceId)
                            }
                            disabled={isAdding || alreadyHasCredential}
                            className="add-device-checkbox"
                          />
                          <div className="add-device-info">
                            <div className="add-device-name">
                              {device.displayName || "Unnamed Device"}
                              {device.serialNumber && (
                                <span className="add-device-serial-inline">
                                  {" "}
                                  ({device.serialNumber})
                                </span>
                              )}
                            </div>
                          </div>
                          {alreadyHasCredential && (
                            <span className="device-already-badge">
                              Already has credential
                            </span>
                          )}
                        </label>
                      </div>
                    );
                  })
                ) : (
                  <div className="no-devices-message">No devices available</div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="add-credential-modal-footer">
          <button
            className="add-credential-cancel-btn"
            onClick={onClose}
            disabled={isAdding}
          >
            Cancel
          </button>
          <button
            className="add-credential-confirm-btn"
            onClick={handleAddCredential}
            disabled={
              isAdding || !selectedCredential || selectedDevices.size === 0
            }
          >
            {isAdding
              ? "Adding..."
              : `Add to ${selectedDevices.size} device${
                  selectedDevices.size !== 1 ? "s" : ""
                }`}
          </button>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmActionModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={performAddCredential}
        title="Confirm Add Credential"
        message={`Are you sure you want to add this credential to ${
          selectedDevices.size
        } device${selectedDevices.size > 1 ? "s" : ""}?`}
        confirmButtonText="Add Credential"
        cancelButtonText="Cancel"
        isProcessing={isAdding}
      />

      {/* Success Modal */}
      <ConfirmActionModal
        isOpen={showSuccessModal}
        onClose={handleSuccessModalClose}
        onConfirm={handleSuccessModalClose}
        title="✅ Success"
        message={successMessage}
        confirmButtonText="OK"
        cancelButtonText={null}
      />
    </div>
  );
};

export default AddCredentialModal;
